/**
 * The walk: containers and the bins inside them, in the order a person walks past
 * them.
 *
 * The order is the core's (`compareWalkOrder`, the same comparator the intake
 * core picks a bin with and the picklist orders a route with), not the client's:
 * a list that agreed with the collection only by luck would put a card in one
 * drawer and go looking in another.
 */

import { compareWalkOrder, formatBinAddress } from '@unsorted/shared';
import type { Bin, Container } from '../api/types.js';

export interface WalkEntry {
  container: Container;
  bin: Bin;
  /** "BOX A / DRAWER 1" - the bin as a place, without a slot. */
  address: string;
  /** 1-based position on the walk, which is what a picklist position means. */
  position: number;
}

export function containerOrder(containers: readonly Container[]): Container[] {
  return [...containers].sort(
    (a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label) || a.publicId.localeCompare(b.publicId),
  );
}

export function binsInWalkOrder(container: Container): Bin[] {
  return [...container.bins].sort(
    (a, b) =>
      compareWalkOrder({ containerSortOrder: 0, binSortOrder: a.sortOrder }, { containerSortOrder: 0, binSortOrder: b.sortOrder }) ||
      a.label.localeCompare(b.label) ||
      a.publicId.localeCompare(b.publicId),
  );
}

/** Every bin, container by container, in the order the walk passes them. */
export function walkOrder(containers: readonly Container[]): WalkEntry[] {
  const entries: WalkEntry[] = [];
  for (const container of containerOrder(containers)) {
    for (const bin of binsInWalkOrder(container)) {
      entries.push({
        container,
        bin,
        address: formatBinAddress(container, bin),
        position: entries.length + 1,
      });
    }
  }
  return entries;
}

export function findEntry(containers: readonly Container[], binPublicId: string): WalkEntry | null {
  return walkOrder(containers).find((entry) => entry.bin.publicId === binPublicId) ?? null;
}
